import { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { useProperties } from '../contexts/PropertyContext';

export const SearchFilters = ({ onSearch, compact = false }) => {
  const { filters, updateFilters, clearFilters, amenities } = useProperties();
  const [localFilters, setLocalFilters] = useState(filters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/properties/cities`
        );
        setCityOptions(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar cidades:', error);
        setCityOptions([]);
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    const fetchNeighborhoods = async () => {
      try {
        const city = localFilters.city || '';
        const url = city
          ? `${process.env.REACT_APP_BACKEND_URL}/api/properties/neighborhoods?city=${encodeURIComponent(city)}`
          : `${process.env.REACT_APP_BACKEND_URL}/api/properties/neighborhoods`;

        const response = await axios.get(url);
        setNeighborhoodOptions(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar bairros:', error);
        setNeighborhoodOptions([]);
      }
    };

    fetchNeighborhoods();
  }, [localFilters.city]);



  const handleChange = (key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    updateFilters(localFilters);
    onSearch?.(localFilters);
  };

  const handleClear = () => {
    const cleared = {
      transaction_type: '',
      property_type: '',
      city: '',
      neighborhood: '',
      price_min: '',
      price_max: '',
      bedrooms_min: '',
      bathrooms_min: '',
      sort_by: 'newest',
    };
    setLocalFilters(cleared);
    clearFilters();
    onSearch?.(cleared);
  };

  const propertyTypes = [
    { value: 'house', label: 'Casa' },
    { value: 'apartment', label: 'Apartamento' },
    { value: 'land', label: 'Terreno' },
    { value: 'commercial', label: 'Comercial' },
    { value: 'rural', label: 'Rural' },
    { value: 'chacara', label: 'Chácara' },

  ];

  const transactionTypes = [
    { value: 'sale', label: 'Comprar' },
    { value: 'rent', label: 'Alugar' },
  ];

  const sortOptions = [
    { value: 'newest', label: 'Mais recentes' },
    { value: 'price_asc', label: 'Menor preço' },
    { value: 'price_desc', label: 'Maior preço' },
    { value: 'area_desc', label: 'Maior área' },
  ];

  return (
    <div data-testid="search-filters" className={`bg-white rounded-2xl shadow-xl ${compact ? 'p-4' : 'p-6 md:p-8'}`}>
      {/* Main search row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1.2fr_1.2fr_180px_60px] gap-4 items-center">
        {/* Transaction Type */}
        <div className="w-full">
          <Select
            value={localFilters.transaction_type || 'all'}
            onValueChange={(v) => handleChange('transaction_type', v === 'all' ? '' : v)}
          >
            <SelectTrigger data-testid="transaction-type-select" className="h-12">
              <SelectValue placeholder="Finalidade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {transactionTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Property Type */}
        <div className="w-full">
          <Select
            value={localFilters.property_type || 'all'}
            onValueChange={(v) => handleChange('property_type', v === 'all' ? '' : v)}
          >
            <SelectTrigger data-testid="property-type-select" className="h-12">
              <SelectValue placeholder="Imóvel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {propertyTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* City */}
        <div className="w-full">
          <Select
            value={localFilters.city || 'all'}
            onValueChange={(value) => {
              handleChange('city', value === 'all' ? '' : value);
              handleChange('neighborhood', '');
            }}
          >
            <SelectTrigger className="h-12 w-full">
              <SelectValue placeholder="Selecione a cidade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as cidades</SelectItem>
              {cityOptions.map((city) => (
                <SelectItem key={city} value={city}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>


        {/* Neighborhood */}
        <div className="w-full">
          <Select
            value={localFilters.neighborhood || 'all'}
            onValueChange={(value) =>
              handleChange('neighborhood', value === 'all' ? '' : value)
            }
          >
            <SelectTrigger className="h-12 w-full">
              <SelectValue
                placeholder={
                  localFilters.city ? 'Selecione o bairro' : 'Todos os bairros'
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {localFilters.city ? 'Todos os bairros da cidade' : 'Todos os bairros'}
              </SelectItem>
              {neighborhoodOptions.map((bairro) => (
                <SelectItem key={bairro} value={bairro}>
                  {bairro}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Search Button */}
        <Button
          onClick={handleSearch}
          className="h-12 w-full lg:w-[180px] bg-brand-blue hover:bg-brand-blue-dark text-white rounded-full"
        >
          <Search className="w-5 h-5 mr-2" />
          Buscar
        </Button>

        {/* Advanced Filters Toggle */}
        <Sheet open={showAdvanced} onOpenChange={setShowAdvanced}>
          <SheetTrigger asChild>
            <Button variant="outline" className="h-12 w-full lg:w-[60px]">
              <SlidersHorizontal className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-heading text-xl">Filtros Avançados</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              {/* Price Range */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Faixa de Preço</Label>
                <div className="flex gap-3">
                  <Input
                    type="number"
                    placeholder="Mínimo"
                    value={localFilters.price_min}
                    onChange={(e) => handleChange('price_min', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Máximo"
                    value={localFilters.price_max}
                    onChange={(e) => handleChange('price_max', e.target.value)}
                  />
                </div>
              </div>

              {/* Bedrooms */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Quartos (mínimo)</Label>
                <Select value={localFilters.bedrooms_min} onValueChange={(v) => handleChange('bedrooms_min', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}+</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bathrooms */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Banheiros (mínimo)</Label>
                <Select value={localFilters.bathrooms_min} onValueChange={(v) => handleChange('bathrooms_min', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}+</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sort */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Ordenar por</Label>
                <Select value={localFilters.sort_by} onValueChange={(v) => handleChange('sort_by', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={handleClear} className="flex-1">
                  <X className="w-4 h-4 mr-2" />
                  Limpar
                </Button>
                <Button onClick={() => { handleSearch(); setShowAdvanced(false); }} className="flex-1 bg-brand-blue hover:bg-brand-blue-dark">
                  Aplicar Filtros
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}; 